import { type FC, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DialogHeader } from "@/shared/components";
import { formatAmountObject } from "@/partner/features/products";
import { PagePaths } from "@/partner/shared/constants/routes";
import { useSubscriptionPurchasesQuery } from "@/partner/entities/subscriptions";
import type { SubscriptionsListQueryType } from "@/types/graphql/partner.generated";
import { interpolationFormat } from "@/partner/shared/lib";
import {
  RENEW_ERROR_REASON_LABEL_KEYS,
  SUBSCRIPTION_STATUS_COLORS,
  SUBSCRIPTION_STATUS_LABEL_KEYS,
  formatPastDue,
} from "../model/subscriptionLabels";

type SubscriptionRow =
  SubscriptionsListQueryType["subscriptions"]["nodes"][number];

interface SubscriptionDetailsDialogProps {
  open: boolean;
  subscription: SubscriptionRow | null;
  onClose: () => void;
}

// Простой лейбл-значение блок — тот же приём, что и в
// PurchaseDetailsDialog/UserDialog (read-only деталка без формы).
const Field: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" component="div">
      {children}
    </Typography>
  </Box>
);

export const SubscriptionDetailsDialog: FC<SubscriptionDetailsDialogProps> = ({
  open,
  subscription,
  onClose,
}) => {
  const { t, i18n } = useTranslation();

  // Список покупок в рамках подписки грузится только пока диалог открыт —
  // subscriptionId нужен для Step 1's фильтра productPurchases.
  const { data: purchasesData, loading: purchasesLoading } =
    useSubscriptionPurchasesQuery({
      variables: { subscriptionId: subscription?.id ?? "", rowsPerPage: 100 },
      skip: !open || !subscription,
      fetchPolicy: "cache-and-network",
    });

  const purchases = purchasesData?.productPurchases?.nodes ?? [];

  const title =
    subscription?.product?.title ?? t("subscriptions.details.title");

  const pastDueProgress = subscription
    ? formatPastDue(subscription, t, (value) =>
        interpolationFormat(value, "dateTime", i18n.language)
      )
    : null;

  return (
    <Dialog fullWidth maxWidth="sm" scroll="body" open={open} onClose={onClose}>
      <DialogHeader title={title} onClose={onClose} />
      <DialogContent sx={{ p: 1 }}>
        <Paper sx={{ p: 2 }}>
          {subscription && (
            <Grid container spacing={2} direction="column">
              <Grid>
                <Field label={t("subscriptions.details.statusLabel")}>
                  <Chip
                    size="small"
                    color={SUBSCRIPTION_STATUS_COLORS[subscription.status]}
                    label={t(
                      SUBSCRIPTION_STATUS_LABEL_KEYS[subscription.status]
                    )}
                  />
                </Field>
              </Grid>
              {/* Просрочка (спека viewer-money): причина и ход попыток. */}
              {pastDueProgress && (
                <Grid>
                  <Field label={t("subscriptions.details.renewErrorLabel")}>
                    {subscription.renewErrorReason
                      ? t(
                          RENEW_ERROR_REASON_LABEL_KEYS[
                            subscription.renewErrorReason
                          ]
                        )
                      : t("common.emptyDash")}
                  </Field>
                </Grid>
              )}
              {pastDueProgress && (
                <Grid>
                  <Field label={t("subscriptions.details.renewAttemptLabel")}>
                    {pastDueProgress}
                  </Field>
                </Grid>
              )}
              <Grid>
                <Field label={t("subscriptions.details.buyerLabel")}>
                  {subscription.buyerEmail ?? "—"}
                </Field>
              </Grid>
              <Grid>
                <Field label={t("subscriptions.details.productLabel")}>
                  {subscription.product ? (
                    <MuiLink
                      component={RouterLink}
                      to={`${PagePaths.products}?tab=edit&id=${subscription.product.id}`}
                    >
                      {subscription.product.title}
                    </MuiLink>
                  ) : (
                    "—"
                  )}
                </Field>
              </Grid>
              {subscription.promocode && (
                <Grid>
                  <Field label={t("subscriptions.details.promocodeLabel")}>
                    {subscription.promocode.code}
                    {subscription.promocode.title && (
                      <Typography variant="body2" color="text.secondary">
                        {subscription.promocode.title}
                      </Typography>
                    )}
                  </Field>
                </Grid>
              )}
              <Grid>
                <Field label={t("subscriptions.details.startAtLabel")}>
                  {interpolationFormat(
                    subscription.startAt,
                    "dateTime",
                    i18n.language
                  )}
                </Field>
              </Grid>
              <Grid>
                <Field label={t("subscriptions.details.currentPeriodEndLabel")}>
                  {interpolationFormat(
                    subscription.currentPeriodEnd,
                    "dateTime",
                    i18n.language
                  )}
                </Field>
              </Grid>
              <Grid>
                <Field
                  label={t("subscriptions.details.billingCyclesCompleteLabel")}
                >
                  {subscription.billingCyclesComplete}
                </Field>
              </Grid>
              <Grid>
                {/* Read-only — партнёр не управляет автопродлением (спека
                    §241), только видит его состояние. */}
                <Field
                  label={t("subscriptions.details.renewSubscriptionLabel")}
                >
                  <Chip
                    size="small"
                    color={
                      subscription.renewSubscription ? "success" : "default"
                    }
                    label={t(
                      subscription.renewSubscription
                        ? "common.yes"
                        : "common.no"
                    )}
                  />
                </Field>
              </Grid>
            </Grid>
          )}
        </Paper>

        {subscription && (
          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {t("subscriptions.details.purchasesTitle")}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {!purchasesLoading && purchases.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("subscriptions.details.purchasesEmpty")}
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("purchases.details.amountLabel")}</TableCell>
                    <TableCell>{t("purchases.details.periodLabel")}</TableCell>
                    <TableCell>{t("purchases.details.startAtLabel")}</TableCell>
                    <TableCell>
                      {t("purchases.details.expiredAtLabel")}
                    </TableCell>
                    <TableCell>
                      {t("purchases.details.refundedLabel")}
                    </TableCell>
                    <TableCell>
                      {t("purchases.details.disputedLabel")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchases.map((purchase) => (
                    <TableRow key={purchase.id}>
                      <TableCell>
                        {formatAmountObject(purchase.payment?.amount, "—")}
                      </TableCell>
                      <TableCell>
                        {purchase.payment
                          ? t("purchases.list.periodDays", {
                              count: purchase.payment.period,
                            })
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {interpolationFormat(
                          purchase.startAt,
                          "short",
                          i18n.language
                        )}
                      </TableCell>
                      <TableCell>
                        {interpolationFormat(
                          purchase.expiredAt,
                          "short",
                          i18n.language
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={purchase.refunded ? "error" : "default"}
                          label={t(
                            purchase.refunded ? "common.yes" : "common.no"
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={purchase.disputed ? "error" : "default"}
                          label={t(
                            purchase.disputed ? "common.yes" : "common.no"
                          )}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
};
